<template>
  <v-row no-gutters align="center">
    <v-col cols="12">
      <v-row no-gutters align="center">
        <v-col cols="auto" class="pr-2 text-left font-weight-medium">
          {{ $i18n.t('admin.smartMatch.profiles') }}
        </v-col>
        <v-col cols="auto" class="pr-2 text-left">
          <base-rounded-button
            icon="mdi-plus"
            size="x-small"
            color="success"
            @click="dialogAction('add')"
            :tooltip="$i18n.t('admin.smartMatch.profileNewTooltip')"
          />
        </v-col>
        <v-col cols="auto" class="text-left">
          <base-button translation-path="dialog.body.import" @click="dialogAction('import')" />
        </v-col>
      </v-row>
      <v-row no-gutters class="pt-7" align="center">
        <v-col cols="12">
          <ribbon-list
            :listItems="profilesArray"
            item-title-key="name"
            item-subtitle-key="dataSourceVerboseName"
            no-items-translation-path="admin.smartMatch.noProfiles"
            style-type="rawHigher"
            :toggleable="false"
          >
            <template #itemButtons-replace="{ item }">
              <base-button
                translation-path="admin.smartMatch.profileComplete"
                @click="dialogAction('complete', item)"
              />
              <dots-menu :items="profileDotsItems" v-on="getProfileDotsListeners(item)" />
            </template>
          </ribbon-list>
        </v-col>
      </v-row>
    </v-col>
    <main-dialog
      v-if="isMainDialogLoaded"
      :is-visible.sync="isMainDialogVisible"
      v-bind="mainDialogBind"
      @agree="dialogActionHandler"
    />
    <main-input-dialog
      ref="main-input-dialog"
      v-if="isMainInputDialogLoaded"
      :is-visible.sync="isMainInputDialogVisible"
      v-bind="mainInputDialogBind"
      @agree="dialogActionHandler"
    >
      <template #fileImport.input>
        <v-row no-gutters>
          <v-col cols="12">
            <file-input
              ref="fileProfileImport"
              :options="fileProfileOptions"
              show-added-files
              :events="{
                'vdropzone-file-added': onFileProfileImportAdded,
                'vdropzone-removed-file': onFileProfileImportRemoved,
              }"
            />
          </v-col>
        </v-row>
      </template>
    </main-input-dialog>
    <main-columns-dialog
      ref="main-columns-dialog"
      v-if="isMainColumnsDialogLoaded"
      :is-visible.sync="isMainColumnsDialogVisible"
      v-bind="mainColumnsDialogBind"
      @valueChange="dialogActionHandler"
      @rowDelete="handlerDeleteCompleteProfile"
      @rowAdd="handlerAddCompleteProfile"
    />
  </v-row>
</template>
<script>
import { call, get } from 'vuex-pathify';
import { get as lodashGet } from 'lodash';

import DotsMenu from '@/components/DotsMenu';
import FileInput from '@/components/FileInput';
import RibbonList from '@/components/RibbonList';

export default {
  name: 'SmartMatchProfiles',
  components: {
    DotsMenu,
    FileInput,
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
    MainColumnsDialog: () => import('@/components/MainColumnsDialog'),
    RibbonList,
  },
  computed: {
    dataSources: get('layers/metadata'),
    dataSourcesNamesArray() {
      return this.$_objectToArray(this.dataSources || []).map(dataSource => {
        return { value: dataSource.name, text: dataSource.verbose_name };
      });
    },
    profiles: get('smartMatch/profiles'),
    profilesArray() {
      return this.$_objectToArray(this.profiles).map(profile => {
        return {
          ...profile,
          dataSourceVerboseName: this.dataSources[profile.datasource]?.verbose_name || '',
        };
      });
    },

    profileDotsItems() {
      return [{ name: 'edit' }, { name: 'export' }, { name: 'delete' }];
    },

    capitalizeCurrentAction() {
      return this.$_capitalize(this.currentAction);
    },
    currentDialogActionData() {
      return this.dialogsActionsData[this.currentAction] || {};
    },
    mainDialogBind() {
      return {
        loading: this.isMainDialogLoading,
        ...(this.isMainDialogVisible
          ? {
              title: this.$i18n.t(`admin.smartMatch.actions.${this.currentAction}ProfileTitle`),
              body: this.$i18n.t(`admin.smartMatch.actions.confirmProfile${this.capitalizeCurrentAction}`, {
                name: this.currentActionData.name,
              }),
              agreeText: this.currentDialogActionData.agreePath,
            }
          : {}),
      };
    },
    mainColumnsDialogBind() {
      return {
        loading: this.isMainColumnsDialogLoading,
        ...(this.isMainColumnsDialogVisible
          ? {
              dialogProps: {
                title: this.$i18n.t(`admin.smartMatch.actions.${this.currentAction}ProfileTitle`, {
                  name: this.currentActionData.name,
                }),
                agreeText: this.currentDialogActionData.agreePath,
                hasAgree: this.currentDialogActionData.hasAgree,
                disagreeText: this.currentDialogActionData.disagreePath,
              },
              rows: this.currentColumnsDialogRows,
              columns: this.currentColumnsDialogColumns,
              columnNameKeyPath: 'path',
              columnVerboseNameKey: 'name',
            }
          : {}),
      };
    },
    mainInputDialogBind() {
      return {
        loading: this.isMainInputDialogLoading,
        dialogWidth: '40%',
        noPaddingsX: true,
        ...(this.isMainInputDialogVisible
          ? {
              titleTranslation: this.$i18n.t(`admin.smartMatch.actions.${this.currentAction}ProfileTitle`),
              agreeText: this.currentDialogActionData.agreePath,
              inputs: this.mainInputDialogInputs,
              customIsValid: this.currentDialogActionData.customIsValid === false ? false : true,
            }
          : {}),
      };
    },
    mainInputDialogInputs() {
      const elementEditInputs = [
        {
          name: 'name',
          translationPath: 'admin.smartMatch.profileName',
          cols: '10',
          leftCol: 4,
          rightCol: 8,
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'text',
          },
          inputClasses: ['px-5'],
        },
        {
          name: 'datasource',
          translationPath: 'admin.smartMatch.profileDataSource',
          cols: '10',
          leftCol: 4,
          rightCol: 8,
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          items: this.dataSourcesNamesArray,
          rowDivider: true,
          inputClasses: ['px-5'],
        },
        {
          name: 'columns',
          translationPath: 'admin.smartMatch.profileColumns',
          cols: '12',
          leftCol: 4,
          rightCol: 8,
          rules: [v => !!v || 'rules.required'],
          dataType: {
            name: 'dict',
            inputs: [
              {
                allowed_data_type: {
                  name: 'text',
                },
                title: this.$i18n.t('admin.smartMatch.systemName'),
                valueKey: 'key',
              },
              {
                allowed_data_type: {
                  name: 'text',
                },
                title: this.$i18n.t('admin.smartMatch.displayName'),
                valueKey: 'name',
              },
            ],
          },
          inputClasses: ['px-5', 'pt-5'],
          newInputProps: {
            hint: this.$i18n.t('admin.smartMatch.profileNewColumnSubtitle'),
          },
        },
      ];
      const elementImportInputs = [
        {
          name: 'name',
          translationPath: 'admin.smartMatch.profileName',
          cols: '10',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'text',
          },
          inputClasses: ['px-5'],
        },
        {
          name: 'datasource',
          translationPath: 'admin.smartMatch.profileDataSource',
          cols: '10',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          items: this.dataSourcesNamesArray,
          inputClasses: ['px-5'],
        },
        {
          name: 'fileImport',
          translationPath: 'admin.smartMatch.file',
          cols: '12',
          inputClasses: ['px-5'],
        },
      ];
      const mainInputDialogInputsDict = {
        add: elementEditInputs,
        edit: elementEditInputs.map(input => {
          if (input.name === 'datasource') {
            return { ...input, disabled: true };
          }
          return input;
        }),
        import: elementImportInputs,
      };
      return (mainInputDialogInputsDict[this.currentAction] || []).map(input => {
        return {
          ...input,
          value: JSON.parse(
            JSON.stringify(this.currentActionData?.[input.name] || (input.dataType?.name === 'dict' ? [] : ''))
          ),
        };
      });
    },

    dialogsActionsData() {
      return {
        add: {
          agreePath: 'dialog.agreeAdd',
          component: 'MainInputDialog',
        },
        delete: {
          agreePath: 'dialog.agreeDelete',
          component: 'MainDialog',
        },
        edit: {
          agreePath: 'dialog.agreeSave',
          component: 'MainInputDialog',
        },
        import: {
          agreePath: 'dialog.body.import',
          component: 'MainInputDialog',
          customIsValid: this.fileProfileImportValid,
        },
        complete: {
          hasAgree: false,
          component: 'MainColumnsDialog',
          initAction: 'setCurrentProfileMappings',
          disagreePath: 'dialog.disagreeClose',
        },
      };
    },
    fileProfileOptions() {
      return {
        url: `${process.env.VUE_APP_API_URL}/smart_match/profile/datasource/import`,
        method: 'post',
        autoProcessQueue: false,
        addRemoveLinks: true,
        paramName: 'file',
        maxFiles: 1,
        acceptedFiles: '.json',
        init: function() {
          this.on('maxfilesexceeded', function(file) {
            this.removeAllFiles();
            this.addFile(file);
          });
        },
      };
    },
  },
  data: () => ({
    currentAction: '',
    currentActionData: undefined,
    currentColumnsDialogColumns: [],
    currentColumnsDialogRows: [],

    fileProfileImportValid: false,

    isMainDialogLoaded: false,
    isMainDialogLoading: false,
    isMainDialogVisible: false,
    isMainInputDialogLoaded: false,
    isMainInputDialogLoading: false,
    isMainInputDialogVisible: false,
    isMainColumnsDialogLoaded: false,
    isMainColumnsDialogLoading: false,
    isMainColumnsDialogVisible: false,
  }),
  methods: {
    addProfile: call('smartMatch/addProfile'),
    addProfileComplete: call('smartMatch/addProfileMapping'),
    deleteProfile: call('smartMatch/deleteProfile'),
    deleteProfileComplete: call('smartMatch/deleteProfileMapping'),
    editProfile: call('smartMatch/editProfile'),
    exportProfile: call('smartMatch/exportProfile'),
    importProfile: call('smartMatch/importProfile'),
    getProfileMappings: call('smartMatch/getProfileMappings'),
    editProfileMapping: call('smartMatch/editProfileMapping'),
    editProfileMappingAttribute: call('smartMatch/editProfileMappingAttribute'),

    async onFileProfileImportAdded(file) {
      const importProfile = JSON.parse(await file.text());
      const index = this.mainInputDialogInputs.findIndex(input => input.name === 'name');
      if (index >= 0 && !this.mainInputDialogInputs[index].value) {
        this.$set(this.mainInputDialogInputs, index, {
          ...this.mainInputDialogInputs[index],
          value: importProfile.name || '',
        });
      }
      this.fileProfileImportValid = true;
    },
    async onFileProfileImportRemoved(file) {
      const importProfile = JSON.parse(await file.text());
      const index = this.mainInputDialogInputs.findIndex(input => input.name === 'name');
      if (index >= 0 && this.mainInputDialogInputs[index]?.value === importProfile.name) {
        this.$set(this.mainInputDialogInputs, index, {
          ...this.mainInputDialogInputs[index],
          value: '',
        });
        this.$refs['main-input-dialog']?.$refs['form']?.resetValidation();
      }
      this.fileProfileImportValid = false;
    },

    getProfileDotsListeners(item) {
      return this.profileDotsItems.reduce((total, action) => {
        total[action.name] = () => {
          this.dialogAction(action.name, item);
        };
        return total;
      }, {});
    },

    async dialogAction(type, value) {
      this.currentAction = type;
      this.currentActionData = value;
      if (!this.dialogsActionsData[type]) {
        this.dialogActionHandler();
        return;
      }
      const { component, initAction } = this.dialogsActionsData[type];
      if (initAction) this[initAction]({ type, value });
      this[`is${component}Loaded`] = true;
      this[`is${component}Loading`] = false;
      this[`is${component}Visible`] = true;
    },
    async dialogActionHandler(data) {
      const dialogComponent = this.currentDialogActionData?.component;
      this[`is${dialogComponent}Loading`] = true;
      try {
        await this[`handler${this.capitalizeCurrentAction}Profile`](data);
        this[`is${dialogComponent}Loading`] = false;
      } catch {
        this[`is${dialogComponent}Loading`] = false;
      }
    },
    async handlerDeleteProfile() {
      await this.deleteProfile(this.currentActionData.id);
      this[`is${this.currentDialogActionData?.component}Visible`] = false;
    },
    async handlerAddProfile() {
      await this.addProfile(this.$_arrayToObject(this.mainInputDialogInputs, 'name', 'value'));
      this[`is${this.currentDialogActionData?.component}Visible`] = false;
    },
    async handlerEditProfile() {
      await this.editProfile({
        id: this.currentActionData.id,
        payload: this.$_filterObjectKeys(this.$_arrayToObject(this.mainInputDialogInputs, 'name', 'value'), {
          notAllowedKeys: 'datasource',
        }),
      });
      this[`is${this.currentDialogActionData?.component}Visible`] = false;
    },
    async handlerExportProfile() {
      await this.exportProfile(this.currentActionData.id);
    },
    async handlerImportProfile() {
      const values = this.$_arrayToObject(this.mainInputDialogInputs, 'name', 'value');
      const importProfile = JSON.parse(
        await this.$refs.fileProfileImport?.$refs.dropzone?.getAcceptedFiles()[0].text()
      );
      const payload = {
        ...importProfile,
        ...this.$_filterObjectKeys(values, {
          allowedKeys: 'name',
        }),
      };
      await this.importProfile({ dataSourceName: values['datasource'], payload });
      this[`is${this.currentDialogActionData?.component}Visible`] = false;
    },
    async handlerCompleteProfile({ column, row, value } = {}) {
      if (column.type === 'property') {
        const payload = {
          [column.path]: value,
        };
        await this.editProfileMapping({ id: row.id, payload });
      } else if (column.type === 'attribute') {
        const attribute = lodashGet(row, `attributes.${column.id}`);
        const payload = {
          ...(attribute ? { id: attribute.id } : { column_id: column.id }),
          attribute: value,
        };
        await this.editProfileMappingAttribute({ id: row.id, payload });
      }
    },
    async handlerDeleteCompleteProfile(row) {
      await this.deleteProfileComplete(row.id);
      await this.setCurrentProfileMappings({ onlyRowsRefresh: true });
    },
    async handlerAddCompleteProfile(row) {
      const payload = { attributes: [] };
      Object.keys(row).forEach(key => {
        const keySplit = key.split('.');
        if (keySplit.length === 1) {
          payload[key] = row[key];
        } else {
          payload.attributes.push({
            column_id: parseInt(keySplit[1]),
            attribute: row[key],
          });
        }
      });
      await this.addProfileComplete({ id: this.currentActionData.id, payload });
      await this.setCurrentProfileMappings({ onlyRowsRefresh: true });
      if (this.$refs['main-columns-dialog']) {
        this.$refs['main-columns-dialog'].newRow = {};
        if (this.$refs['main-columns-dialog'].$refs['form-row-new']) {
          this.$refs['main-columns-dialog'].$refs['form-row-new'].resetValidation();
        }
      }
    },

    async setCurrentProfileMappings({
      value = JSON.parse(JSON.stringify(this.currentActionData)),
      onlyRowsRefresh = false,
    } = {}) {
      try {
        const profileMappings = await this.getProfileMappings(value.id);
        this.currentColumnsDialogRows = profileMappings.map(profileMapping => {
          return {
            ...profileMapping,
            attributes: profileMapping.attributes.reduce((acc, item) => {
              if (item.column_id) acc[item.column_id] = item;
              return acc;
            }, {}),
          };
        });
        if (!onlyRowsRefresh) {
          const dataSourceAttributes = this.dataSources[value.datasource]?.attributes_schema.attributes.map(
            attribute => {
              return { text: attribute.verbose_name, value: attribute.name };
            }
          );
          this.currentColumnsDialogColumns = [
            ...[
              {
                name: this.$i18n.t('admin.smartMatch.profileId'),
                type: 'property',
                path: 'id',
                columnWidth: '150px',
              },
              {
                name: this.$i18n.t('admin.smartMatch.profileKey'),
                type: 'property',
                path: 'key',
                columnWidth: '200px',
                dataInput: {
                  dataType: { name: 'text' },
                  rules: [v => !!v || ''],
                  hideDetails: true,
                },
                filter: true,
              },
              {
                name: this.$i18n.t('admin.smartMatch.profileVerboseName'),
                type: 'property',
                path: 'name',
                columnWidth: '300px',
                dataInput: {
                  dataType: { name: 'text' },
                  hideDetails: true,
                },
                optional: true,
                filter: true,
              },
            ],
            ...value.columns.map(column => {
              return {
                ...column,
                type: 'attribute',
                path: `attributes.${column.id}.attribute`,
                dataInput: {
                  dataType: { name: 'select' },
                  items: dataSourceAttributes,
                  hideDetails: true,
                },
                filter: true,
              };
            }),
          ];
        }
      } finally {
        //
      }
    },
  },
  mounted() {
    this.$watch(
      vm => [vm.isMainDialogVisible, vm.isMainInputDialogVisible, vm.isMainColumnsDialogVisible],
      nV => {
        const [isMainDialogVisible, isMainInputDialogVisible, isMainColumnsDialogVisible] = nV;
        if (!isMainDialogVisible && !isMainInputDialogVisible && !isMainColumnsDialogVisible) {
          this.currentAction = '';
          this.currentActionData = undefined;
          this.currentColumnsDialogRows = [];
          this.currentColumnsDialogColumns = [];
          this.isMainDialogLoading = false;
          this.isMainInputDialogLoading = false;
          this.isMainColumnsDialogLoading = false;
          this.$refs.fileProfileImport?.$refs.dropzone.removeAllFiles();
        }
      }
    );
  },
};
</script>
